import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import apiService from '../services/api.service';
import { useAuth } from './AuthContext';

// Definizione tipi
interface Permissions {
  [scope: string]: {
    [action: string]: boolean;
  };
}

interface PermissionsContextType {
  permissions: Permissions;
  visibility: string;
  maxDiscountPercent: number;
  canSendQuotes: boolean;
  isLoading: boolean;
  hasPermission: (scope: string, action: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

// Valori di default
const defaultContext: PermissionsContextType = {
  permissions: {},
  visibility: 'none',
  maxDiscountPercent: 0,
  canSendQuotes: false,
  isLoading: true,
  hasPermission: () => false,
  refreshPermissions: async () => {},
};

const PermissionsContext = createContext<PermissionsContextType>(defaultContext);

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [permissions, setPermissions] = useState<Permissions>({});
  const [visibility, setVisibility] = useState<string>('none');
  const [maxDiscountPercent, setMaxDiscountPercent] = useState<number>(0);
  const [canSendQuotes, setCanSendQuotes] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions({});
      setVisibility('none');
      setMaxDiscountPercent(0);
      setCanSendQuotes(false);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const data = await apiService.getMyPermissions();
      setPermissions(data.permissions || {});
      setVisibility(data.visibility || 'none');
      setMaxDiscountPercent(data.maxDiscountPercent || 0);
      setCanSendQuotes(data.canSendQuotes !== false);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // In caso di errore, mantieni i permessi vuoti
      setPermissions({});
      setVisibility('none');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = useCallback((scope: string, action: string): boolean => {
    const scopePermissions = permissions[scope];
    if (!scopePermissions) return false;
    return scopePermissions[action] === true;
  }, [permissions]);

  const refreshPermissions = useCallback(async () => {
    await fetchPermissions();
  }, [fetchPermissions]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    permissions,
    visibility,
    maxDiscountPercent,
    canSendQuotes,
    isLoading,
    hasPermission,
    refreshPermissions,
  }), [permissions, visibility, maxDiscountPercent, canSendQuotes, isLoading, hasPermission, refreshPermissions]);

  return (
    <PermissionsContext.Provider value={contextValue}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

// Hook helper per controllare permessi specifici
export const useHasPermission = (scope: string, action: string): boolean => {
  const { hasPermission } = usePermissions();
  return hasPermission(scope, action);
};

// Costanti per scope e actions (mirror del backend)
export const SCOPES = {
  CLIENTS: 'clients',
  CONTACTS: 'contacts',
  LEADS: 'leads',
  QUOTES: 'quotes',
  QUOTES_DRAFT: 'quotes_draft',
  QUOTES_SENT: 'quotes_sent',
  QUOTES_ACCEPTED: 'quotes_accepted',
  PRODUCTS: 'products',
  PRICE_LISTS: 'price_lists',
  DISCOUNTS: 'discounts',
  MARGINS: 'margins',
  BUNDLES: 'bundles',
  TEMPLATES: 'templates',
  DOCUMENTS: 'documents',
  APPROVALS: 'approvals',
  DISCOUNT_APPROVAL: 'discount_approval',
  USERS: 'users',
  ROLES: 'roles',
  SETTINGS: 'settings',
  INTEGRATIONS: 'integrations',
  AUDIT_LOG: 'audit_log',
  REPORTS: 'reports',
  ANALYTICS: 'analytics',
  DASHBOARD: 'dashboard',
  // Team Commerciale
  SALESPEOPLE: 'salespeople',
  SALES_LEVELS: 'sales_levels',
  APPROVAL_RULES: 'approval_rules',
  DISCOUNT_MATRIX: 'discount_matrix',
  CUSTOMER_TYPE: 'customer_type',
};

export const ACTIONS = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  APPROVE: 'approve',
  EXPORT: 'export',
  IMPORT: 'import',
};

export const VISIBILITY = {
  NONE: 'none',
  SELF: 'self',
  TEAM: 'team',
  ALL: 'all',
  TENANT: 'tenant',
};
